import React, { lazy, Suspense, useEffect, useRef } from "react";
import { Switch, Route, Redirect } from "react-router-dom";
import { useSelector } from "react-redux";
import Loading from "components/shared-components/Loading";
import { APP_PREFIX_PATH } from "configs/AppConfig";
import JwtAuthService from "services/JwtAuthService";
import MenuService from "services/MenuService";
import navigationConfig from "configs/NavigationConfig";

// Função para verificar se o usuário tem permissão
const verificarPermissoes = (permissoes, listaPermissoes) => {
	if (permissoes === undefined || permissoes === null) return true;

	if (!Array.isArray(permissoes)) permissoes = [permissoes];

	// Retorna true caso tenha pelo menos uma das permissões
	for (const p of permissoes) {
		const temPermissao = !p || listaPermissoes[p];
		if (temPermissao) {
			return true;
		}
	}

	return false;
};

// Função recursiva para encontrar o primeiro item do menu com permissão
const encontrarPrimeiraPaginaPermitida = (menuItems, listaPermissoes) => {
	for (const item of menuItems) {
		// Verifica se o item tem permissão
		if (verificarPermissoes(item.permissao, listaPermissoes)) {
			// Se tem path e não tem submenu, retorna o path
			if (item.path && (!item.submenu || item.submenu.length === 0)) {
				return item.path;
			}
			// Se tem submenu, verifica recursivamente
			if (item.submenu && item.submenu.length > 0) {
				const pathPermitido = encontrarPrimeiraPaginaPermitida(
					item.submenu,
					listaPermissoes,
				);
				if (pathPermitido) {
					return pathPermitido;
				}
			}
		}
	}
	return null;
};

// Componente para redirecionar para a primeira página permitida
const RedirectToFirstAllowedPage = () => {
	const listaPermissoes = useSelector((state) => state.permissoes);
	const primeiraPagina = encontrarPrimeiraPaginaPermitida(
		navigationConfig,
		listaPermissoes,
	);

	// Se não encontrou nenhuma página permitida, redireciona para dashboard (comportamento padrão)
	const destino = primeiraPagina || `${APP_PREFIX_PATH}/dashboard`;

	return <Redirect to={destino} />;
};

export const AppViews = () => {
	let intervalId = useRef(null);

	const populateMenu = async () => {
		try {
			if (JwtAuthService.isLogged()) {
				await MenuService.populateMenuData();
			}
		} catch (e) {}

		intervalId.current = setInterval(async () => {
			if (JwtAuthService.isLogged()) {
				MenuService.populateMenuData();
			}
		}, 30000);
	};

	useEffect(() => {
		populateMenu();

		return () => {
			clearInterval(intervalId.current);
		};
	}, []);

	return (
		<Suspense fallback={<Loading cover="content" />}>
			<Switch>
				<Route
					path={`${APP_PREFIX_PATH}/dashboard`}
					component={lazy(() => import(`./dashboard`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/dashboard-financeiro`}
					component={lazy(() => import(`./dashboard-financeiro`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/dashboard-operacional`}
					component={lazy(() => import(`./dashboard-operacional`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/dados-conta`}
					component={lazy(() => import(`./dados-conta`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-auditoria-registro`}
					component={lazy(() => import(`./lista-auditoria-registro`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-usuarios`}
					component={lazy(() => import(`./lista-usuarios`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/usuario/:id`}
					component={lazy(() => import(`./usuario`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/usuario`}
					component={lazy(() => import(`./usuario`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-grupos-usuario`}
					component={lazy(() => import(`./lista-grupos-usuario`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/grupo-usuario/:id`}
					component={lazy(() => import(`./grupo-usuario`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/grupo-usuario`}
					component={lazy(() => import(`./grupo-usuario`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-grupos-rateio`}
					component={lazy(() => import(`./lista-grupos-seguro`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/grupo-seguro/:id`}
					component={lazy(() => import(`./grupo-seguro`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/grupo-seguro`}
					component={lazy(() => import(`./grupo-seguro`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-tipo-veiculo`}
					component={lazy(() => import(`./lista-tipo-veiculo`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/tipo-veiculo/:id`}
					component={lazy(() => import(`./tipo-veiculo`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/tipo-veiculo`}
					component={lazy(() => import(`./tipo-veiculo`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-especie-veiculo`}
					component={lazy(() => import(`./lista-especie-veiculo`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/especie-veiculo/:id`}
					component={lazy(() => import(`./especie-veiculo`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/especie-veiculo`}
					component={lazy(() => import(`./especie-veiculo`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-composicao-veiculo`}
					component={lazy(() => import(`./lista-composicao-veiculo`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/composicao-veiculo/:id`}
					component={lazy(() => import(`./composicao-veiculo`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/composicao-veiculo`}
					component={lazy(() => import(`./composicao-veiculo`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-tipo-sinistro`}
					component={lazy(() => import(`./lista-tipo-sinistro`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/tipo-sinistro/:id`}
					component={lazy(() => import(`./tipo-sinistro`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/tipo-sinistro`}
					component={lazy(() => import(`./tipo-sinistro`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-tipo-despesa-sinistro`}
					component={lazy(
						() => import(`./lista-tipo-despesa-sinistro`),
					)}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/tipo-despesa-sinistro/:id`}
					component={lazy(() => import(`./tipo-despesa-sinistro`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/tipo-despesa-sinistro`}
					component={lazy(() => import(`./tipo-despesa-sinistro`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-tipo-documento`}
					component={lazy(() => import(`./lista-tipo-documento`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/tipo-documento/:id`}
					component={lazy(() => import(`./tipo-documento`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/tipo-documento`}
					component={lazy(() => import(`./tipo-documento`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-estados`}
					component={lazy(() => import(`./lista-estados`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/estado/:id`}
					component={lazy(() => import(`./estado`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/estado`}
					component={lazy(() => import(`./estado`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-cidades`}
					component={lazy(() => import(`./lista-cidades`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/cidade/:id`}
					component={lazy(() => import(`./cidade`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/cidade`}
					component={lazy(() => import(`./cidade`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-integrante`}
					component={lazy(() => import(`./lista-integrante`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/integrante/:id`}
					component={lazy(() => import(`./integrante`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/detalhes-integrante/:id`}
					component={lazy(() => import(`./detalhes-integrante`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/integrante`}
					component={lazy(() => import(`./integrante`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/veiculo/:id`}
					component={lazy(() => import(`./veiculo`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/detalhes-veiculo/:id`}
					component={lazy(() => import(`./detalhes-veiculo`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/veiculo`}
					component={lazy(() => import(`./veiculo`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-vistoriador`}
					component={lazy(() => import(`./lista-vistoriador`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/vistoriador/:id`}
					component={lazy(() => import(`./vistoriador`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/vistoriador`}
					component={lazy(() => import(`./vistoriador`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-sinistros`}
					component={lazy(() => import(`./lista-sinistros`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/sinistro/:id`}
					component={lazy(() => import(`./sinistro`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/sinistro`}
					component={lazy(() => import(`./sinistro`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-veiculo`}
					component={lazy(() => import(`./lista-veiculo`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-modelo-veiculo`}
					component={lazy(() => import(`./lista-modelo-veiculo`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/modelo-veiculo/:id`}
					component={lazy(() => import(`./modelo-veiculo`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/modelo-veiculo`}
					component={lazy(() => import(`./modelo-veiculo`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-vistoria`}
					component={lazy(() => import(`./lista-vistoria`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-motoristas`}
					component={lazy(() => import(`./lista-motoristas`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/motorista/:id`}
					component={lazy(() => import(`./motorista`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/motorista`}
					component={lazy(() => import(`./motorista`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-paises`}
					component={lazy(() => import(`./lista-paises`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/pais/:id`}
					component={lazy(() => import(`./pais`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/pais`}
					component={lazy(() => import(`./pais`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-fornecedores`}
					component={lazy(() => import(`./lista-fornecedores`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/fornecedor/:id`}
					component={lazy(() => import(`./fornecedor`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/fornecedor`}
					component={lazy(() => import(`./fornecedor`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-financeiras`}
					component={lazy(() => import(`./lista-financeiras`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/financeira/:id`}
					component={lazy(() => import(`./financeira`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/financeira`}
					component={lazy(() => import(`./financeira`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/financeiro`}
					component={lazy(() => import(`./financeiro`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/financeiro-rateio`}
					component={lazy(() => import(`./financeiro-rateio`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-vendedores`}
					component={lazy(() => import(`./lista-vendedores`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/vendedor/:id`}
					component={lazy(() => import(`./vendedor`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/vendedor`}
					component={lazy(() => import(`./vendedor`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-fipe`}
					component={lazy(() => import(`./lista-fipe`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-penalizacoes`}
					component={lazy(() => import(`./lista-penalizacoes`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/penalizacao/:id`}
					component={lazy(() => import(`./penalizacao`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/penalizacao`}
					component={lazy(() => import(`./penalizacao`))}
				/>
				{/*<Route*/}
				{/*	path={`${APP_PREFIX_PATH}/lista-tipos-vistoria`}*/}
				{/*	component={lazy(() => import(`./lista-tipos-vistoria`))}*/}
				{/*/>*/}
				{/*<Route*/}
				{/*	path={`${APP_PREFIX_PATH}/tipo-vistoria/:id`}*/}
				{/*	component={lazy(() => import(`./tipo-vistoria`))}*/}
				{/*/>*/}
				<Route
					path={`${APP_PREFIX_PATH}/tipo-vistoria`}
					component={lazy(() => import(`./tipo-vistoria`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/ferramentas`}
					component={lazy(() => import(`./ferramentas`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-marca-veiculo`}
					component={lazy(() => import(`./lista-marca-veiculo`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/marca-veiculo/:id`}
					component={lazy(() => import(`./marca-veiculo`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/marca-veiculo`}
					component={lazy(() => import(`./marca-veiculo`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-integrante-veiculo`}
					component={lazy(() => import(`./lista-integrante-veiculo`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-juridico`}
					component={lazy(() => import(`./lista-juridico`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/conta-receber`}
					component={lazy(() => import(`./conta-receber`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-financeiro`}
					component={lazy(() => import(`./lista-financeiro`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/nova-cobranca`}
					component={lazy(() => import(`./nova-cobranca`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/conta-pagar/:id`}
					component={lazy(() => import(`./conta-pagar`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/conta-pagar`}
					component={lazy(() => import(`./conta-pagar`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-contas-pagar`}
					component={lazy(() => import(`./lista-contas-pagar`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-contas-receber`}
					component={lazy(() => import(`./lista-contas-receber`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/configuracao-financeira`}
					component={lazy(() => import(`./configuracao-financeira`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-taxas-administracao`}
					component={lazy(
						() => import(`./lista-taxas-administracao`),
					)}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/taxa-administracao/:id`}
					component={lazy(() => import(`./taxa-administracao`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/taxa-administracao`}
					component={lazy(() => import(`./taxa-administracao`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-veiculo-precificacao`}
					component={lazy(
						() => import(`./lista-veiculo-precificacao`),
					)}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-veiculo-pre-cadastro`}
					component={lazy(
						() => import(`./lista-veiculo-pre-cadastro`),
					)}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/lista-veiculo-com-pendencias`}
					component={lazy(
						() => import(`./lista-veiculo-com-pendencias`),
					)}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/relatorios`}
					component={lazy(() => import(`./relatorios`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/relatorio-des-cadastro`}
					component={lazy(() => import(`./relatorio-des-cadastro`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/relatorio-composicao-rateio`}
					component={lazy(
						() => import(`./relatorio-composicao-rateio`),
					)}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/relatorio-composicao-rateio-manual`}
					component={lazy(
						() => import(`./relatorio-composicao-rateio-manual`),
					)}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/relatorio-composicao-rateio-manual-gestao`}
					component={lazy(
						() =>
							import(
								`./relatorio-composicao-rateio-manual-gestao`
							),
					)}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/relatorio-taxas-emitidas`}
					component={lazy(() => import(`./relatorio-taxas-emitidas`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/relatorio-rateio-vigencia`}
					component={lazy(
						() => import(`./relatorio-rateio-vigencia`),
					)}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/relatorio-integrante-veiculo`}
					component={lazy(
						() => import(`./relatorio-integrante-veiculo`),
					)}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/relatorio-veiculos-tipo-especie`}
					component={lazy(
						() => import(`./relatorio-veiculos-tipo-especie`),
					)}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/contrato-participacao`}
					component={lazy(() => import(`./contrato-participacao`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/relatorio-cotas-individuais`}
					component={lazy(
						() => import(`./relatorio-cotas-individuais`),
					)}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/relatorio-indice-padrao-participacao`}
					component={lazy(
						() => import(`./relatorio-indice-padrao-participacao`),
					)}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/relatorio-integrante-veiculo-rastreamento`}
					component={lazy(
						() =>
							import(
								`./relatorio-integrante-veiculo-rastreamento`
							),
					)}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/relatorio-idade-media-frota`}
					component={lazy(
						() => import(`./relatorio-idade-media-frota`),
					)}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/relatorio-cobrancas-vencidas`}
					component={lazy(
						() => import(`./relatorio-cobrancas-vencidas`),
					)}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/relatorio-cobrancas-a-vencer`}
					component={lazy(
						() => import(`./relatorio-cobrancas-a-vencer`),
					)}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/relatorio-valores-pagos`}
					component={lazy(() => import(`./relatorio-valores-pagos`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/relatorio-despesas-sinistro`}
					component={lazy(
						() => import(`./relatorio-despesas-sinistro`),
					)}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/relatorio-despesa-por-placa`}
					component={lazy(
						() => import(`./relatorio-despesa-por-placa`),
					)}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/relatorio-comercial`}
					component={lazy(() => import(`./relatorio-comercial`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/relatorio-mensal-sinistros`}
					component={lazy(
						() => import(`./relatorio-mensal-sinistros`),
					)}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/relatorio-rastreadores`}
					component={lazy(() => import(`./relatorio-rastreadores`))}
				/>
				<Route
					path={`${APP_PREFIX_PATH}/central-envios`}
					component={lazy(() => import(`./central-envios`))}
				/>
				<Route
					exact
					path={`${APP_PREFIX_PATH}`}
					component={RedirectToFirstAllowedPage}
				/>
			</Switch>
		</Suspense>
	);
};

export default React.memo(AppViews);
